import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { 
  Image, Video, Upload, Trash2, ExternalLink, Save, RefreshCw, 
  Home, Info, GraduationCap, Play, Link, FileVideo, ImageIcon,
  GripVertical, Plus, Images, ArrowUp, ArrowDown, Crop, RotateCcw
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { siteMediaAPI } from '../lib/api';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default media URLs (current site media)
const DEFAULT_MEDIA = {
  'landing.hero_video': {
    media_type: 'video',
    url: 'https://customer-assets.emergentagent.com/job_1d578f26-1f3f-468c-870b-210440301216/artifacts/dalqb9go_UPA%20ad%20small.MOV.mov',
    alt_text: 'UPA Hero Video'
  },
  'landing.hero_image': {
    media_type: 'image',
    url: 'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/4is8revz_040.JPG',
    alt_text: 'Hero fallback image'
  },
  'about.hero': {
    media_type: 'image',
    url: 'https://customer-assets.emergentagent.com/job_class-manager-84/artifacts/4is8revz_040.JPG',
    alt_text: 'About page hero'
  },
  'about.video': {
    media_type: 'video',
    url: 'https://customer-assets.emergentagent.com/job_1d578f26-1f3f-468c-870b-210440301216/artifacts/dalqb9go_UPA%20ad%20small.MOV.mov',
    alt_text: 'About page video'
  },
  'classes.hero': {
    media_type: 'image',
    url: 'https://customer-assets.emergentagent.com/job_upa-class-manager/artifacts/0og2wefe_a-Legally-Blonde-289.JPG',
    alt_text: 'Classes page hero'
  }
};

const SiteMedia = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const [uploads, setUploads] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [activeTab, setActiveTab] = useState('landing');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [editingGalleryImage, setEditingGalleryImage] = useState(null);
  
  // Image cropping state
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropTarget, setCropTarget] = useState(null);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 90, height: 90, x: 5, y: 5 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const cropImageRef = useRef(null);
  const [cropLoading, setCropLoading] = useState(false);
  const [newGalleryUrl, setNewGalleryUrl] = useState('');
  const [newGalleryAlt, setNewGalleryAlt] = useState('');
  
  const fileInputRef = useRef(null);
  const galleryFileRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, uploadsRes, galleryRes] = await Promise.all([
        siteMediaAPI.getSettings(),
        siteMediaAPI.getUploads(),
        siteMediaAPI.getGallery(),
      ]);
      
      // Merge with defaults
      const mergedSettings = { ...DEFAULT_MEDIA };
      Object.entries(settingsRes.data || {}).forEach(([key, value]) => {
        mergedSettings[key] = value;
      });
      setSettings(mergedSettings);
      setUploads(uploadsRes.data || []);
      
      // Check if gallery has default IDs (not in database) and seed them
      const galleryImages = galleryRes.data?.images || [];
      if (galleryImages.length > 0 && galleryImages[0].id?.startsWith('default')) {
        // Seed defaults into database
        try {
          await siteMediaAPI.seedGalleryDefaults();
          const newGalleryRes = await siteMediaAPI.getGallery();
          setGallery(newGalleryRes.data?.images || []);
        } catch (e) {
          setGallery(galleryImages);
        }
      } else {
        setGallery(galleryImages);
      }
    } catch (error) {
      console.error('Failed to load site media:', error);
      setSettings(DEFAULT_MEDIA);
    } finally {
      setLoading(false);
    }
  };

  const getSetting = (page, section) => {
    const key = `${page}.${section}`;
    return settings[key] || DEFAULT_MEDIA[key] || { page, section, media_type: 'image', url: '', embed_url: '', alt_text: '' };
  };

  const updateSetting = (page, section, field, value) => {
    const key = `${page}.${section}`;
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...getSetting(page, section),
        page,
        section,
        [field]: value
      }
    }));
  };

  const saveSetting = async (page, section) => {
    setSaving(true);
    try {
      const setting = getSetting(page, section);
      await siteMediaAPI.updateSetting({
        page: page,
        section: section,
        media_type: setting.media_type,
        url: setting.url,
        embed_url: setting.embed_url,
        alt_text: setting.alt_text
      });
      toast.success('Media setting saved!');
    } catch (error) {
      toast.error('Failed to save setting');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) {
      console.log('No file selected or no upload target');
      return;
    }

    console.log('Uploading file:', file.name, file.type, file.size);
    setUploading(true);
    try {
      const mediaType = file.type.startsWith('video/') ? 'video' : 'image';
      console.log('Calling upload API with media type:', mediaType);
      const response = await siteMediaAPI.uploadFile(file, mediaType);
      console.log('Upload response:', response.data);
      
      const fullUrl = `${API_URL}${response.data.url}`;
      
      if (uploadTarget.isGallery) {
        // Add to gallery
        await siteMediaAPI.addGalleryImage({
          url: fullUrl,
          alt_text: file.name,
          sort_order: gallery.length
        });
        toast.success('Image added to gallery!');
      } else {
        updateSetting(uploadTarget.page, uploadTarget.section, 'url', fullUrl);
        updateSetting(uploadTarget.page, uploadTarget.section, 'media_type', mediaType);
        
        await siteMediaAPI.updateSetting({
          page: uploadTarget.page,
          section: uploadTarget.section,
          media_type: mediaType,
          url: fullUrl,
          embed_url: null,
          alt_text: getSetting(uploadTarget.page, uploadTarget.section).alt_text
        });
        toast.success('File uploaded and saved!');
      }
      
      setUploadModalOpen(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchData();
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Error response:', error.response?.data);
      // Handle different error formats
      let errorMsg = 'Upload failed';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors come as array
          errorMsg = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMsg = detail.msg || detail.message || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const openUploadModal = (page, section, acceptType, isGallery = false) => {
    setUploadTarget({ page, section, acceptType, isGallery });
    setUploadModalOpen(true);
  };

  // Image cropping functions
  const openCropModal = (page, section, imageUrl) => {
    setCropTarget({ page, section });
    // Use proxy URL to avoid CORS issues when drawing to canvas
    const proxyUrl = `${API_URL}/api/proxy-image?url=${encodeURIComponent(imageUrl)}`;
    setCropImageSrc(proxyUrl);
    setCrop({ unit: '%', width: 90, height: 90, x: 5, y: 5 });
    setCompletedCrop(null);
    setCropModalOpen(true);
  };

  const onCropComplete = useCallback((crop, percentCrop) => {
    setCompletedCrop(percentCrop);
  }, []);

  const getCroppedImg = useCallback(async () => {
    if (!completedCrop || !cropImageRef.current) return null;

    const image = cropImageRef.current;
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    const pixelCrop = {
      x: (completedCrop.x / 100) * image.width * scaleX,
      y: (completedCrop.y / 100) * image.height * scaleY,
      width: (completedCrop.width / 100) * image.width * scaleX,
      height: (completedCrop.height / 100) * image.height * scaleY,
    };

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  }, [completedCrop]);

  const handleCropSave = async () => {
    setCropLoading(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (!croppedBlob) {
        toast.error('Please select a crop area');
        return;
      }

      // Create a file from the blob
      const file = new File([croppedBlob], 'cropped-image.jpg', { type: 'image/jpeg' });
      
      // Upload the cropped image
      const response = await siteMediaAPI.uploadFile(file, 'image');
      const uploadedUrl = response.data?.url;
      
      if (uploadedUrl && cropTarget) {
        const fullUrl = uploadedUrl.startsWith('/api/') 
          ? `${API_URL}${uploadedUrl}` 
          : uploadedUrl;
          
        await siteMediaAPI.updateSetting({
          page: cropTarget.page,
          section: cropTarget.section,
          media_type: 'image',
          url: fullUrl,
          embed_url: null,
          alt_text: getSetting(cropTarget.page, cropTarget.section).alt_text
        });
        
        toast.success('Cropped image saved!');
        setCropModalOpen(false);
        fetchData();
      }
    } catch (error) {
      console.error('Crop save error:', error);
      toast.error('Failed to save cropped image');
    } finally {
      setCropLoading(false);
    }
  };

  const handleAddGalleryImage = async () => {
    if (!newGalleryUrl) {
      toast.error('Please enter an image URL');
      return;
    }
    
    try {
      await siteMediaAPI.addGalleryImage({
        url: newGalleryUrl,
        alt_text: newGalleryAlt || 'Gallery image',
        sort_order: gallery.length
      });
      toast.success('Image added to gallery!');
      setNewGalleryUrl('');
      setNewGalleryAlt('');
      setGalleryModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add image');
    }
  };

  const handleUpdateGalleryImage = async (id, url, alt) => {
    try {
      await siteMediaAPI.updateGalleryImage(id, { url, alt_text: alt, sort_order: 0 });
      toast.success('Image updated!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update image');
    }
  };

  const handleDeleteGalleryImage = async (id) => {
    if (!confirm('Delete this gallery image?')) return;
    try {
      await siteMediaAPI.deleteGalleryImage(id);
      toast.success('Image deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const moveGalleryImage = async (index, direction) => {
    const newGallery = [...gallery];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newGallery.length) return;
    
    [newGallery[index], newGallery[newIndex]] = [newGallery[newIndex], newGallery[index]];
    setGallery(newGallery);
    
    try {
      await siteMediaAPI.reorderGallery(newGallery.map(img => img.id));
    } catch (error) {
      fetchData();
    }
  };

  const MediaEditor = ({ page, section, title, acceptType = 'image/*,video/*', showEmbed = false }) => {
    const setting = getSetting(page, section);
    const isVideo = setting.media_type === 'video' || setting.media_type === 'embed';
    const hasMedia = setting.url || setting.embed_url;
    
    return (
      <Card className="bg-[#111] border-[#333]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            {isVideo ? <Video className="w-5 h-5 text-[#FF007F]" /> : <Image className="w-5 h-5 text-[#7000FF]" />}
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Preview */}
          <div className="relative aspect-video bg-[#0a0a0a] rounded-lg overflow-hidden border border-[#222]">
            {setting.media_type === 'embed' && setting.embed_url ? (
              <iframe
                src={setting.embed_url}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : setting.media_type === 'video' && setting.url ? (
              <video src={setting.url} className="w-full h-full object-cover" controls />
            ) : setting.url ? (
              <img src={setting.url} alt={setting.alt_text || title} className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-white/30">
                <ImageIcon className="w-12 h-12 mb-2" />
                <span>No media set</span>
              </div>
            )}
            
            {hasMedia && (
              <div className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded text-xs text-white/70">
                {setting.media_type === 'embed' ? 'Embed' : setting.media_type === 'video' ? 'Video' : 'Image'}
              </div>
            )}
          </div>

          {/* Media Type Selector */}
          <div className="space-y-2">
            <Label className="text-white/70">Media Type</Label>
            <Select 
              value={setting.media_type} 
              onValueChange={(v) => updateSetting(page, section, 'media_type', v)}
            >
              <SelectTrigger className="bg-[#0a0a0a] border-[#333] text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#333]">
                <SelectItem value="image">
                  <span className="flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Image</span>
                </SelectItem>
                <SelectItem value="video">
                  <span className="flex items-center gap-2"><FileVideo className="w-4 h-4" /> Video File</span>
                </SelectItem>
                {showEmbed && (
                  <SelectItem value="embed">
                    <span className="flex items-center gap-2"><Play className="w-4 h-4" /> YouTube/Vimeo Embed</span>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* URL Input or Embed */}
          {setting.media_type === 'embed' ? (
            <div className="space-y-2">
              <Label className="text-white/70">Embed URL (YouTube/Vimeo)</Label>
              <div className="flex gap-2">
                <Input
                  value={setting.embed_url || ''}
                  onChange={(e) => updateSetting(page, section, 'embed_url', e.target.value)}
                  placeholder="https://www.youtube.com/embed/..."
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <p className="text-xs text-white/40">
                YouTube: https://www.youtube.com/embed/VIDEO_ID | Vimeo: https://player.vimeo.com/video/ID
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <Label className="text-white/70">
                {setting.media_type === 'video' ? 'Video URL' : 'Image URL'}
              </Label>
              <div className="flex gap-2">
                <Input
                  value={setting.url || ''}
                  onChange={(e) => updateSetting(page, section, 'url', e.target.value)}
                  placeholder="https://..."
                  className="bg-[#0a0a0a] border-[#333] text-white flex-1"
                />
                <Button
                  variant="outline"
                  className="border-[#333] text-white hover:bg-[#222]"
                  onClick={() => openUploadModal(page, section, acceptType)}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
                {setting.url && setting.media_type === 'image' && (
                  <Button
                    variant="outline"
                    className="border-[#7000FF]/50 text-[#7000FF] hover:bg-[#7000FF]/10"
                    onClick={() => openCropModal(page, section, setting.url)}
                  >
                    <Crop className="w-4 h-4 mr-2" />
                    Crop
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Alt Text */}
          <div className="space-y-2">
            <Label className="text-white/70">Alt Text / Description</Label>
            <Input
              value={setting.alt_text || ''}
              onChange={(e) => updateSetting(page, section, 'alt_text', e.target.value)}
              placeholder="Describe this media..."
              className="bg-[#0a0a0a] border-[#333] text-white"
            />
          </div>

          {/* Save Button */}
          <Button 
            onClick={() => saveSetting(page, section)}
            disabled={saving}
            className="w-full bg-gradient-to-r from-[#FF007F] to-[#7000FF] hover:opacity-90"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />
      <Toaster position="top-right" richColors />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF007F] to-[#7000FF] rounded-xl flex items-center justify-center">
              <Image className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">
                Site Media
              </h1>
              <p className="text-white/60">Manage images and videos across the site</p>
            </div>
          </div>
          
          <Button 
            variant="outline" 
            onClick={fetchData} 
            className="border-[#333] text-white hover:bg-[#222]"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Page Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#111] border border-[#333] p-1 mb-6">
            <TabsTrigger value="landing" className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white">
              <Home className="w-4 h-4 mr-2" />
              Landing Page
            </TabsTrigger>
            <TabsTrigger value="about" className="data-[state=active]:bg-[#7000FF] data-[state=active]:text-white">
              <Info className="w-4 h-4 mr-2" />
              About Page
            </TabsTrigger>
            <TabsTrigger value="classes" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
              <GraduationCap className="w-4 h-4 mr-2" />
              Classes Page
            </TabsTrigger>
            <TabsTrigger value="gallery" className="data-[state=active]:bg-green-500 data-[state=active]:text-white">
              <Images className="w-4 h-4 mr-2" />
              Gallery
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="grid md:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-96 bg-[#111] rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              {/* Landing Page Tab */}
              <TabsContent value="landing">
                <div className="grid md:grid-cols-2 gap-6">
                  <MediaEditor 
                    page="landing" 
                    section="hero_video" 
                    title="Hero Video / Background" 
                    acceptType="video/*"
                    showEmbed={true}
                  />
                  <MediaEditor 
                    page="landing" 
                    section="hero_image" 
                    title="Hero Fallback Image (Poster)" 
                    acceptType="image/*"
                  />
                </div>
              </TabsContent>

              {/* About Page Tab */}
              <TabsContent value="about">
                <div className="grid md:grid-cols-2 gap-6">
                  <MediaEditor 
                    page="about" 
                    section="hero" 
                    title="About Hero Image" 
                    acceptType="image/*"
                  />
                  <MediaEditor 
                    page="about" 
                    section="video" 
                    title="About Video Section" 
                    acceptType="video/*"
                    showEmbed={true}
                  />
                </div>
              </TabsContent>

              {/* Classes Page Tab */}
              <TabsContent value="classes">
                <div className="grid md:grid-cols-2 gap-6">
                  <MediaEditor 
                    page="classes" 
                    section="hero" 
                    title="Classes Hero Banner" 
                    acceptType="image/*"
                  />
                </div>
              </TabsContent>

              {/* Gallery Tab */}
              <TabsContent value="gallery">
                <Card className="bg-[#111] border-[#333]">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white flex items-center gap-2">
                        <Images className="w-5 h-5 text-green-400" />
                        Gallery Carousel Images ({gallery.length})
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          className="border-[#333] text-white hover:bg-[#222]"
                          onClick={() => openUploadModal('gallery', 'image', 'image/*', true)}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload
                        </Button>
                        <Button
                          className="bg-green-500 hover:bg-green-600"
                          onClick={() => setGalleryModalOpen(true)}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Add Image URL
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-white/50 text-sm mb-4">
                      These images appear in the rotating gallery carousel on the landing page. Drag to reorder or use arrows.
                    </p>
                    
                    {gallery.length === 0 ? (
                      <p className="text-white/50 text-center py-12">No gallery images. Add some above!</p>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                        {gallery.map((img, index) => (
                          <div key={img.id} className="relative group">
                            <div className="aspect-square bg-[#0a0a0a] rounded-lg overflow-hidden border border-[#222]">
                              <img 
                                src={img.url} 
                                alt={img.alt_text || `Gallery ${index + 1}`} 
                                className="w-full h-full object-cover"
                              />
                            </div>
                            
                            {/* Overlay controls */}
                            <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center gap-2 p-2">
                              <div className="flex gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-white hover:bg-white/20 h-8 w-8"
                                  onClick={() => moveGalleryImage(index, 'up')}
                                  disabled={index === 0}
                                >
                                  <ArrowUp className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-white hover:bg-white/20 h-8 w-8"
                                  onClick={() => moveGalleryImage(index, 'down')}
                                  disabled={index === gallery.length - 1}
                                >
                                  <ArrowDown className="w-4 h-4" />
                                </Button>
                              </div>
                              
                              <div className="flex gap-1">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-white hover:bg-white/20 h-8 w-8"
                                  onClick={() => {
                                    navigator.clipboard.writeText(img.url);
                                    toast.success('URL copied!');
                                  }}
                                >
                                  <Link className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="text-red-400 hover:bg-red-500/20 h-8 w-8"
                                  onClick={() => handleDeleteGalleryImage(img.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            
                            {/* Position badge */}
                            <div className="absolute top-2 left-2 bg-black/70 px-2 py-1 rounded text-xs text-white/70">
                              #{index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </>
          )}
        </Tabs>

        {/* Uploaded Files Section */}
        <Card className="mt-8 bg-[#111] border-[#333]">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#FF007F]" />
              Your Uploaded Files
            </CardTitle>
          </CardHeader>
          <CardContent>
            {uploads.length === 0 ? (
              <p className="text-white/50 text-center py-8">No files uploaded yet. Upload files to use across the site.</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {uploads.map(upload => (
                  <div key={upload.id} className="relative group">
                    <div className="aspect-square bg-[#0a0a0a] rounded-lg overflow-hidden border border-[#222]">
                      {upload.media_type === 'video' ? (
                        <video src={`${API_URL}${upload.path}`} className="w-full h-full object-cover" />
                      ) : (
                        <img src={`${API_URL}${upload.path}`} alt={upload.original_name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 rounded-lg">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-white hover:bg-white/20"
                        onClick={() => {
                          navigator.clipboard.writeText(`${API_URL}${upload.path}`);
                          toast.success('URL copied!');
                        }}
                      >
                        <Link className="w-4 h-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-red-400 hover:bg-red-500/20"
                        onClick={async () => {
                          if (confirm('Delete this file?')) {
                            await siteMediaAPI.deleteUpload(upload.id);
                            fetchData();
                            toast.success('File deleted');
                          }
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-white/50 mt-1 truncate">{upload.original_name}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="bg-[#111] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle>Upload {uploadTarget?.isGallery ? 'Gallery Image' : 'Media'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div 
              className="border-2 border-dashed border-[#333] rounded-lg p-8 text-center cursor-pointer hover:border-[#FF007F]/50 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="w-12 h-12 text-white/30 mx-auto mb-4" />
              <p className="text-white/70 mb-2">Click to select a file</p>
              <p className="text-white/40 text-sm">
                Images: JPG, PNG, WebP, GIF (max 10MB)<br />
                Videos: MP4, WebM, MOV (max 100MB)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept={uploadTarget?.acceptType || 'image/*,video/*'}
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
            {uploading && (
              <div className="flex items-center justify-center gap-2 text-[#FF007F]">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Uploading...</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadModalOpen(false)} className="border-[#333]">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Gallery Image Modal */}
      <Dialog open={galleryModalOpen} onOpenChange={setGalleryModalOpen}>
        <DialogContent className="bg-[#111] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle>Add Gallery Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Image URL</Label>
              <Input
                value={newGalleryUrl}
                onChange={(e) => setNewGalleryUrl(e.target.value)}
                placeholder="https://..."
                className="bg-[#0a0a0a] border-[#333] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label>Alt Text (optional)</Label>
              <Input
                value={newGalleryAlt}
                onChange={(e) => setNewGalleryAlt(e.target.value)}
                placeholder="Describe this image..."
                className="bg-[#0a0a0a] border-[#333] text-white"
              />
            </div>
            {newGalleryUrl && (
              <div className="aspect-video bg-[#0a0a0a] rounded-lg overflow-hidden border border-[#222]">
                <img src={newGalleryUrl} alt="Preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGalleryModalOpen(false)} className="border-[#333]">
              Cancel
            </Button>
            <Button onClick={handleAddGalleryImage} className="bg-green-500 hover:bg-green-600">
              Add to Gallery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Crop Modal */}
      <Dialog open={cropModalOpen} onOpenChange={setCropModalOpen}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crop className="w-5 h-5 text-[#7000FF]" />
              Crop Image
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-white/50">
              Drag to select the area you want to keep. The cropped image will replace the current one.
            </p>
            
            {cropImageSrc && (
              <div className="flex justify-center bg-[#0a0a0a] rounded-lg p-4 max-h-[60vh] overflow-auto">
                <ReactCrop
                  crop={crop}
                  onChange={(_, percentCrop) => setCrop(percentCrop)}
                  onComplete={onCropComplete}
                >
                  <img
                    ref={cropImageRef}
                    src={cropImageSrc}
                    alt="Crop preview"
                    style={{ maxHeight: '50vh', maxWidth: '100%' }}
                    crossOrigin="anonymous"
                  />
                </ReactCrop>
              </div>
            )}
            
            <div className="flex items-center justify-between text-sm text-white/50">
              <span>
                Crop: {crop.width?.toFixed(0)}% × {crop.height?.toFixed(0)}%
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCrop({ unit: '%', width: 90, height: 90, x: 5, y: 5 })}
                className="text-white/50 hover:text-white"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCropModalOpen(false)}
              className="border-[#333] text-white hover:bg-[#222]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCropSave}
              disabled={cropLoading || !completedCrop}
              className="bg-gradient-to-r from-[#7000FF] to-[#FF007F]"
            >
              {cropLoading ? 'Saving...' : 'Save Cropped Image'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SiteMedia;
